import { forwardRef, memo, useCallback, useMemo, useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import Autocomplete from '@shared/components/autocomplete/Autocomplete';
import { TextField } from '@shared/views/form-dialog';
import InputAdornment from '@material-ui/core/InputAdornment';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ProductLocationList from './ProductLocationList';
import GalleryIcon from '@resources/icons/GalleryIcon';
import CloseIcon from '@material-ui/icons/Close';
import themeColor from 'theme';

const useStyles = makeStyles((theme) => ({
  textFieldRoot: {
    height: '42px',
    width: '200px',
    "& > div.MuiAutocomplete-inputRoot[class*='MuiOutlinedInput-root']": {
      height: '42px',
      padding: '0 12px !important',
      '& > input': {
        height: '42px',
        padding: '9.5px 4px 9.5px 25px',
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#C3D0E1',
      },
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderWidth: '1px',
    },
    '& > .MuiAutocomplete-option:last-child': {
      color: '#000',
    },
  },
  arrowOpen: {
    transform: 'rotate(180deg)',
    '& path': {
      fill: '#192A3E',
    },
  },
  arrowClosed: {
    transform: 'rotate(0deg)',
    '& path': {
      fill: '#192A3E',
    },
  },
  valueSelected: {
    '& > div > .MuiOutlinedInput-notchedOutline': {
      borderColor: `${theme.palette.primary.main} !important`,
    },
    '& > div > input': {
      color: theme.palette.primary.main,
    },
    '& .MuiInputAdornment-root .MuiSvgIcon-root path': {
      fill: theme.palette.primary.main,
    },
  },
  customOption: {
    fontStyle: 'italic',
    color: '#000',
    display: 'inline-flex',
    all: 'revert',
  },
  placeholder: {
    display: 'flex',
    alignItems: 'center',
    position: 'absolute',
    color: '#192A3E',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 500,
    '& svg': {
      marginRight: '8px',
    },
  },
  clickableAdornment: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    '&:hover > svg': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
      borderRadius: '50%',
      transition: 'fill 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    },
  },
}));

const CUSTOM_INPUT_OPTION = {
  name: 'custom prompt',
  label: 'custom prompt',
  value: '',
};

const LocationTextField = forwardRef(({
  name = 'location',
  label,
  onChange,
  value,
  ...props
}, ref) => {
  const classes = useStyles();
  const [inputValue, setInputValue] = useState('');
  const [isCustomInput, setIsCustomInput] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [open, setOpen] = useState(false);

  const options = useMemo(() => [
    ...ProductLocationList,
    CUSTOM_INPUT_OPTION,
  ], []);

  const findOptionByValue = useCallback((val) => {
    if (!val) return null;
    return options.find(option => option.value === val) || null;
  }, [options]);

  const currentValue = useMemo(() => findOptionByValue(value), [value, findOptionByValue]);
  const hasValue = !!value;
  const showClearIcon = isCustomInput && hasValue;

  useEffect(() => {
    if (value) {
      const option = findOptionByValue(value);
      if (option) {
        setIsCustomInput(false);
        setInputValue(option.label);
      } else {
        setIsCustomInput(true);
        setInputValue(value);
      }
    } else {
      setInputValue('');
    }
  }, [value, findOptionByValue]);

  const handleChange = useCallback((_, newValue) => {
    if (newValue?.name === CUSTOM_INPUT_OPTION.name) {
      setIsCustomInput(true);
      setInputValue('');
      onChange('');
    } else if (typeof newValue === 'string') {
      setIsCustomInput(true);
      onChange(newValue);
    } else if (newValue?.value !== undefined) {
      setIsCustomInput(false);
      onChange(newValue.value);
    } else {
      setIsCustomInput(false);
      onChange('');
    }
  }, [onChange]);

  const handleInputChange = useCallback((_, newInputValue) => {
    if (newInputValue === '') {
      onChange('');
      setIsCustomInput(false);
    } else if (isCustomInput) {
      onChange(newInputValue);
    }
  }, [isCustomInput, onChange]);

  const filterOptions = useCallback((options, { inputValue }) => {
    return options.filter(option => option.label.toLowerCase().includes(inputValue.toLowerCase()));
  }, []);

  const handleDropdownOpen = () => {
    setIsDropdownOpen(true);
    setOpen(true);
  };

  const handleDropdownClose = () => {
    setIsDropdownOpen(false);
    setOpen(false);
  };

  const handleClearClick = () => {
    onChange('');
    setInputValue('');
    setIsCustomInput(false);
    setOpen(open);
  };

  const handleAdornmentClick = () => {
    if (showClearIcon) {
      handleClearClick();
    } else {
      setOpen(prev => !prev);
      setIsDropdownOpen(prev => !prev);
    }
  };

  const renderInput = useCallback((params) => {
    const showPlaceholder = !hasValue && !params.inputProps.value && !isCustomInput;
    const iconColor = hasValue ? themeColor.palette.primary.main : '#192A3E';

    return (
      <TextField
        { ...params }
        classes={ {
          root: classNames(classes.textFieldRoot, {
            [classes.valueSelected]: hasValue,
          }),
        } }
        label={ label }
        variant="outlined"
        InputLabelProps={ { shrink: true } }
        InputProps={ {
          ...params.InputProps,
          startAdornment: showPlaceholder ? (
            <div
              className={ classes.placeholder }
              onClick={ () => setOpen(true) }
            >
              <GalleryIcon fill={ iconColor } />
              Location
            </div>
          ) : (
            <div className={ classes.placeholder }>
              <GalleryIcon fill={ iconColor } />
            </div>
          ),
          endAdornment: (
            <>
              { params.InputProps.endAdornment }
              <InputAdornment
                position="end"
                className={ classes.clickableAdornment }
                onClick={ handleAdornmentClick }
              >
                {showClearIcon ? (
                  <CloseIcon fontSize="small" className={ classes.arrowClosed } />
                ) : (
                  <ArrowDropDownIcon
                    className={ isDropdownOpen ? classes.arrowOpen : classes.arrowClosed }
                  />
                )}
              </InputAdornment>
            </>
          ),
        } }
        placeholder={ isCustomInput ? 'Enter custom location' : undefined }
      />
    );
  }, [classes, hasValue, showClearIcon, isCustomInput, isDropdownOpen]);

  return (
    <Autocomplete
      { ...props }
      freeSolo
      disableClearable
      inputValue={ inputValue }
      options={ options }
      filterOptions={ filterOptions }
      getOptionLabel={ (option) => typeof option === 'string' ? option : option.label }
      isOptionEqualToValue={ (option, val) => option?.value === val?.value }
      onChange={ handleChange }
      onInputChange={ handleInputChange }
      ref={ ref }
      value={ currentValue }
      open={ open }
      onOpen={ handleDropdownOpen }
      onClose={ handleDropdownClose }
      renderInput={ renderInput }
    />
  );
});

export default memo(LocationTextField);
